import React from 'react';
import { AlertTriangle, CheckCircle, AlertCircle, Info } from 'lucide-react';

const ConfirmModal = ({
  isOpen,
  title = 'Are you sure?',
  message = 'Please confirm this action.',
  onConfirm,
  onCancel,
  danger = false,
  type = danger ? 'danger' : 'success',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
}) => {
  if (!isOpen) return null;

  const isDanger = danger || type === 'danger';
  const isSuccess = type === 'success' && !danger;
  const isWarning = type === 'warning';

  let iconBg = '#FEF2F2';
  let iconBorder = '#FEE2E2';
  let iconColor = '#EF4444';
  let confirmBtnBg = '#EF4444';
  let confirmBtnHover = '#DC2626';
  let IconComponent = AlertTriangle;

  if (isSuccess) {
    iconBg = '#ECFDF5';
    iconBorder = '#D1FAE5';
    iconColor = '#07A95B';
    confirmBtnBg = '#07A95B';
    confirmBtnHover = '#059669';
    IconComponent = CheckCircle;
  } else if (isWarning) {
    iconBg = '#FFFBEB';
    iconBorder = '#FEF3C7';
    iconColor = '#F59E0B';
    confirmBtnBg = '#F59E0B';
    confirmBtnHover = '#D97706';
    IconComponent = AlertCircle;
  } else if (type === 'info') {
    iconBg = '#EFF6FF';
    iconBorder = '#DBEAFE';
    iconColor = '#3B82F6';
    confirmBtnBg = '#3B82F6';
    confirmBtnHover = '#2563EB';
    IconComponent = Info;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onCancel}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(17, 24, 39, 0.55)',
          zIndex: 9998,
          backdropFilter: 'blur(4px)',
          animation: 'confirmFadeIn 0.18s ease-out',
        }}
      />

      {/* Modal Dialog */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '32px 28px',
          zIndex: 9999,
          width: '90%',
          maxWidth: '440px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
          fontFamily: "'Inter', sans-serif",
          animation: 'confirmScaleIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '16px' }}>
          {/* Icon Badge */}
          <div
            style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              backgroundColor: iconBg,
              border: `2px solid ${iconBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            }}
          >
            <IconComponent size={28} color={iconColor} strokeWidth={2.2} />
          </div>

          {/* Title & Message */}
          <div>
            <h3
              style={{
                margin: '0 0 8px',
                fontSize: '19px',
                fontWeight: 700,
                color: '#111827',
                letterSpacing: '-0.01em',
              }}
            >
              {title}
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: '14px',
                color: '#4B5563',
                lineHeight: 1.55,
              }}
            >
              {message}
            </p>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', width: '100%', marginTop: '8px' }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                flex: 1,
                padding: '11px 16px',
                borderRadius: '10px',
                border: '1px solid #D1D5DB',
                backgroundColor: '#FFFFFF',
                color: '#374151',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                outline: 'none',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F9FAFB';
                e.currentTarget.style.borderColor = '#9CA3AF';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#FFFFFF';
                e.currentTarget.style.borderColor = '#D1D5DB';
              }}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              style={{
                flex: 1,
                padding: '11px 16px',
                borderRadius: '10px',
                border: 'none',
                backgroundColor: confirmBtnBg,
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                boxShadow: isDanger
                  ? '0 4px 12px rgba(239, 68, 68, 0.25)'
                  : '0 4px 12px rgba(7, 169, 91, 0.25)',
                outline: 'none',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = confirmBtnHover;
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = confirmBtnBg;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes confirmFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes confirmScaleIn {
          from { opacity: 0; transform: translate(-50%, -46%) scale(0.96); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
      `}</style>
    </>
  );
};

export default ConfirmModal;
